<script lang="ts" setup generic="T extends RecordSchema<any>">
	import type { DynamicTableContext, RecordSchema } from '~/types'
	import type { SelectValue } from 'ant-design-vue/es/select'
	import type { InputProps } from 'ant-design-vue'
	import usePurchaseApi from '~/services/apis/purchases-info'
	import StringMethods from '~/utils/character'

	type SubmissionContext = { [id: string]: number | string }
	type EventListeners = {
		validateEvent: {
			param: Record<string, DynamicTableRecord>
			callback: (arg: boolean) => void
			event: 'validator'
		}
	}

	type Props = {
		options?: TableFilterOptions
		records: RecordsFn<T>
		tableColumns: TableColumn[]
		required?: boolean
		skipUserVerification?: boolean
		deletable?: boolean
		addNewField?: boolean
		loading?: boolean
		submitting?: boolean
		editable?: boolean
		useOptionMenu?: boolean
		verifyEmail?: boolean
		viewSlot?: boolean
		onValidator?: Function
	}

	const props = withDefaults(defineProps<Props>(), {
		verifyEmail: true
	})
	const { loading } = toRefs(props)
	const emits = defineEmits<{
		(event: 'update:value', param: DynamicTableContext[]): void
		(arg: 'done', param: SubmissionContext[]): void
		(arg: 'nextPage', param: number): void
		(arg: 'update:viewSlot', param: boolean): void
		(arg: 'add-field', param?: unknown): Promise<void>
		(
			arg: 'validator',
			param: EventListeners['validateEvent']['param'],
			callback: EventListeners['validateEvent']['callback']
		): Promise<boolean>
		(arg: 'update-records', param: unknown): Promise<void>
	}>()

	const { verifyTableUserRecord } = usePurchaseApi()
	const { stripUnderScore } = StringMethods()
	const {
		currentPage,
		isFetching: loadingData,
		paginationMeta: paginationList,
		tableRecord
	} = usePaginator<T>({
		options: computed(() => props.options),
		records: props.records
	})

	const fieldRecords = ref<DynamicTableContext[]>([])
	const formElement = ref<HTMLFormElement | null>(null)

	const isAnItemDone = computed((): boolean => {
		return Object.values(tableRecord.value).some(item => item.done)
	})

	const tableMenu = computed(() => {
		return {
			edit: {
				condition: true,
				callback: (record?: unknown[]) => {
					editRecord(record?.[0] as number)
				},
				spanClass: ''
			},
			'add new field': {
				condition: props.addNewField && isAnItemDone.value,
				callback: () => {
					validateForm().then(() => {
						emits('add-field')
					})
				},
				spanClass: 'text-primary-2 '
			},
			'remove field': {
				condition: props.deletable && tableRecord.value.length > 1,
				callback: (i?: unknown[]) => removeField(i?.[0] as number),
				spanClass: 'text-error-500'
			}
		}
	})

	const handleSelectChange = (e: SelectValue, index: number, record: DynamicTableContext, columnKey: string) => {
		const response = recordResponse(record, columnKey)
		if (response) {
			response.value = String(e)
			if (typeof response?.onChange === 'function') {
				;(response.onChange as Function)(e, index, record)
			}
		}
	}

	const handleInputChange = (e: Event, index: number, record: DynamicTableContext, columnKey: string) => {
		const response = recordResponse(record, columnKey)
		const htmlEl = e.target as HTMLInputElement
		if (response && htmlEl) {
			response.value = htmlEl.value
			if (typeof response?.onChange === 'function' && htmlEl?.value) {
				;(response.onChange as Function)(htmlEl.value, index, record)
			}
		}
	}

	async function validateForm(): Promise<{ [id: string]: DynamicTableContext }> {
		try {
			const res = await formElement.value?.validate()
			return Promise.resolve(res)
		} catch (error) {
			return Promise.reject(error)
		}
	}

	const isRecordEmpty = (obj: DynamicTableContext): boolean => {
		const { done, editable, isActive, sn, ...rest } = obj
		if (rest && typeof rest === 'object') {
			for (const key in rest) {
				const res = rest[key]
				if (
					typeof res === 'object' &&
					(res?.value === undefined ||
						res?.value === null ||
						res.value === 'n/a' ||
						String(res.value).trim() === '')
				) {
					return true
				}
			}
		}
		return false
	}

	const recordResponse = (record: DynamicTableContext, key: string) => {
		return record[key] as DynamicTableRecord
	}

	const extractFormValues = (records = fieldRecords.value) => {
		const dataForm: SubmissionContext[] = []
		const resultList = records.filter(item => item.done)
		// Structure the data to strip validation keys
		for (let index = 0; index < resultList.length; index++) {
			const record = resultList[index]
			const emptyRecord: SubmissionContext = {}
			for (const key in record) {
				const value = record[key]
				const valuesResult =
					typeof value === 'object' ? value.value : typeof value === 'boolean' ? 'true' : value
				if (valuesResult) {
					Object.defineProperty(emptyRecord, key, {
						value: String(valuesResult).trim(),
						writable: true,
						enumerable: true
					})
				}
			}
			dataForm.push(emptyRecord)
		}
		return Promise.resolve(dataForm)
	}

	const isEmailAndPhoneLinked = async (payload: { email: string; phone: string }) => {
		if (props.verifyEmail) {
			const res = await verifyTableUserRecord({ ...payload, phone: replacePhoneNumberToNaija(payload.phone) })
			if (res?.data) {
				const isLinked = res.data
				return isLinked
			}
		}
		return false
	}

	const validateTableField = (record: DynamicTableContext) => {
		const responseRecord = record as { [id: string]: DynamicTableRecord }
		for (const key in record) {
			if (responseRecord[key]?.validations) {
				let message = ''
				const validations = responseRecord[key].validations
				if (validations) {
					if (validations.required && !responseRecord[key].value) {
						message = String(validations?.message || `${stripUnderScore(key)} is required`)
					} else if (validations.min && String(responseRecord[key].value).length < validations.min) {
						message =
							(validations?.message as string) ||
							`${stripUnderScore(key)} must be at least ${validations.min} characters long`
					} else if (validations.max && String(responseRecord[key].value).length > validations.max) {
						message =
							(validations?.message as string) ||
							`${stripUnderScore(key)} must be at less or equal to ${validations.min}`
					}
				}
				if (message) {
					useNotification().alert({
						status: 400,
						message
					})
					return Promise.reject(message)
				}
			}
		}
		return Promise.resolve(true)
	}
	const isFieldValidated = (param: DynamicTableContext, fn: (res: boolean) => void) => {
		if (!props?.onValidator) {
			fn(true)
		}
		param.loading = true
		emits('validator', param as Record<string, DynamicTableRecord>, async response => {
			try {
				const result = await response
				if (result) {
					param.loading = false
					fn(true)
				}
			} catch (error) {
				console.warn(error)
			} finally {
				param.loading = false
			}
		})
	}
	const verifyEmailInfo = async (param: DynamicTableContext, index: number) => {
		if (props?.skipUserVerification) {
			return true
		}
		try {
			const fieldResponse = param as Record<string, DynamicTableRecord>

			const verificationPayload = {
				email: String(fieldResponse?.email?.value ?? ''),
				phone: String(fieldResponse?.phone?.value ?? fieldResponse?.phone_number?.value ?? ''),
				role_number: Number(index) + 1
			}
			param.loading = true
			try {
				const accountNotLinked = await isEmailAndPhoneLinked(verificationPayload)
				if (accountNotLinked) {
					return true
				}
				return false
			} finally {
				param.loading = false
			}
		} catch (e) {
			console.error(e)
		}
		return false
	}

	function addField(record?: DynamicTableContext, index = 0) {
		const responseRecord = record as { [id: string]: DynamicTableRecord }
		isFieldValidated(responseRecord, async canProceed => {
			if (canProceed) {
				const process = async () => {
					const canAdd = await validateTableField(responseRecord)
					if (record && canAdd) {
						const verified = await verifyEmailInfo(record, index)
						if (verified) {
							record.done = true
							record.editable = false
							record.isActive = false
							const formResult = await extractFormValues()
							emits('update:value', formResult)
						}
					}
				}

				if (props.required) {
					const canPass = await validateForm()
					if (canPass) {
						await process()
					}
					return null
				} else if (record && !isRecordEmpty(record)) {
					await process()
					return null
				}
				useNotification().alert({
					status: 400,
					message: 'Field not added, something went wrong.'
				})
			}
		})
	}

	async function editRecord(i: number) {
		const record = tableRecord.value[i]
		if (record) {
			;(record as DynamicTableContext).done = false
			;(record as DynamicTableContext).editable = true
		}
		const formResult = await extractFormValues()
		emits('update:value', formResult)
	}

	async function removeField(index: number) {
		if (tableRecord.value.length > 1) {
			const filteredResult = tableRecord.value.filter((_, i) => i !== index)
			tableRecord.value = filteredResult
			const formResult = await extractFormValues()
			emits('update-records', formResult)
			emits('update:value', formResult)
		}
	}

	const updateRecord = (index: number) => {
		const list = tableRecord.value
		const record = list[index] as DynamicTableContext
		isFieldValidated(record, async canProceed => {
			if (canProceed && !isRecordEmpty(record)) {
				const canAdd = await validateTableField(record)
				if (canAdd) {
					const verified = await verifyEmailInfo(record, index)
					if (verified) {
						record.isActive = false
						record.editable = false
						record.done = true
						const formResult = await extractFormValues(list)
						emits('update:value', formResult)
					}
				}
			}
		})
	}

	const saveForm = () => {
		if (props.required) {
			validateForm().then(() => {
				extractFormValues().then(dataForm => {
					emits('done', dataForm)
				})
			})
		} else {
			extractFormValues().then(dataForm => {
				emits('done', dataForm)
			})
		}
	}

	const proceedToNextPage = (arg: number) => {
		if (arg === currentPage.value) {
			return null
		}
		if (fieldRecords.value.every(item => item.done)) {
			emits('nextPage', arg)
		} else {
			useNotification().alert({
				message: `Page: ${currentPage.value} still has empty slot. Max it out to proceed.`,
				status: 200
			})
		}
	}

	watch(
		tableRecord,
		result => {
			if (result) {
				fieldRecords.value = result
			}
		},
		{
			deep: true,
			immediate: true
		}
	)
</script>

<template>
	<div class="w-full">
		<div class="flex-items flex w-full flex-col justify-center">
			<slot name="note" />

			<shared-table-loader v-slot="{ columns }" :loading="loading || loadingData" :columns="tableColumns">
				<a-form
					v-show="!viewSlot"
					ref="formElement"
					:model="fieldRecords"
					class="w-full !max-w-full !px-0"
					autocomplete="off"
					:validate-messages="antValidateMessages"
					hide-required-mark
				>
					<shared-dynamic-table
						:loading="loading"
						:table-columns="columns"
						:table-record="fieldRecords"
						class="w-full overflow-x-auto"
					>
						<template #body="{ index, column, record }">
							<div
								v-if="column.key === 'sn'"
								class="flex items-center justify-center font-medium"
								:class="{
									'bg-white': typeof record[column.key] === 'boolean',
									'min-h-[70px] text-gray-400': !record.done
								}"
							>
								{{ index + 1 }}
							</div>

							<template v-else-if="!['action', 'sn'].includes(String(column.key))">
								<template v-if="!record?.isActive && !record?.editable">
									<div
										v-if="Boolean(column.key)"
										class="flex min-h-[70px] w-full items-center px-5 text-gray-400 sm:min-w-[150px]"
									>
										<slot
											:name="`filled-${column.key}`"
											:record="record"
											:column="String(column.key)"
											:value="recordResponse(record, String(column.key))?.value"
										>
											{{ recordResponse(record, String(column.key))?.value ?? '---' }}
										</slot>
									</div>
								</template>

								<template v-else>
									<template v-if="typeof record[String(column.key)] === 'object'">
										<a-form-item
											:name="[index, String(column.key), 'value']"
											:required="recordResponse(record, String(column.key))?.required"
											:rules="[
												{
													type: String(column.key) === 'email' ? 'email' : undefined
												},
												recordResponse(record, String(column.key))?.validations || {}
											]"
											class="error-absolute !mb-0"
										>
											<slot
												:name="column.key"
												:records="records"
												:record="record"
												:column="column.key"
												:index="index"
												:set-record="recordResponse"
											>
												<lazy-a-select
													v-if="recordResponse(record, String(column.key))?.type === 'select'"
													:options="recordResponse(record, String(column.key))?.options || []"
													:name="String(column.key)"
													:aria-label="`${String(column.key)}-${index}`"
													:data-testid="`${String(column.key)}-${index}`"
													:placeholder="
														recordResponse(record, column.key)?.placeholder || '-'
													"
													:class="[
														recordResponse(record, column.key)?.class || '',
														'custom-select'
													]"
													:show-search="recordResponse(record, column.key)?.searchable"
													:disabled="
														!fieldRecords[index]?.editable ||
														recordResponse(record, column.key)?.disabled
													"
													:value="(recordResponse(record, column.key)?.value as any) || ''"
													:filter-option="filterSelectOption"
													@change="
														(e: any) => handleSelectChange(e, index, record, column.key)
													"
												/>

												<lazy-a-input
													v-else
													:id="column.key"
													v-number="
														['value', 'number'].includes(
															recordResponse(record, column.key)?.type || ''
														)
													"
													v-tel="
														['tel', 'phone'].includes(
															recordResponse(record, column.key)?.type || ''
														)
													"
													v-format="recordResponse(record, column.key)?.format"
													v-naira="recordResponse(record, column.key)?.currencyFormat"
													autocomplete="none"
													:aria-label="column.key"
													:data-testid="`${column.key}-${index}`"
													:maxlength="recordResponse(record, column.key)?.validations?.max"
													:type="
														(recordResponse(record, column.key)?.type ||
															'text') as InputProps['type']
													"
													:name="column.key"
													:placeholder="
														recordResponse(record, column.key)?.placeholder || '-'
													"
													:value="(recordResponse(record, column.key)?.value as string) || ''"
													:inputmode="recordResponse(record, column.key)?.inputMode || 'text'"
													:disabled="
														!fieldRecords[index]?.editable ||
														recordResponse(record, column.key)?.disabled
													"
													class="custom-input placeholder:text-center placeholder:font-extrabold placeholder:text-gray-950"
													:class="[recordResponse(record, column.key)?.class || '']"
													@change="
														(e: any) => handleInputChange(e, index, record, column.key)
													"
												/>
											</slot>
										</a-form-item>
									</template>
								</template>
							</template>

							<!-- Table Actions -->
							<div v-else class="flex justify-center">
								<template v-if="!record.isUsed">
									<a-button
										v-if="record.isActive && !useOptionMenu"
										class="00 default-btn-sm !bg-primary-2 !text-white shadow-md disabled:!bg-primary-2 disabled:shadow-none"
										:disabled="isRecordEmpty(record)"
										:loading="record?.loading"
										:data-testid="`add-${index}`"
										@click.prevent="addField(record, index)"
									>
										<lazy-tools-icon dir="other" icon="plus" class="mr-2" />
										Add
									</a-button>

									<a-button
										v-else-if="
											(useOptionMenu &&
												!isRecordEmpty(record) &&
												index !== 0 &&
												record.isActive) ||
											(index == 0 && record.isActive)
										"
										:data-testid="`add-${index}`"
										:loading="record?.loading"
										class="00 default-btn-sm !bg-primary-2 !text-white shadow-md disabled:!bg-primary-2 disabled:shadow-none"
										:disabled="isRecordEmpty(record)"
										@click.prevent="addField(record)"
									>
										<lazy-tools-icon dir="other" icon="plus" class="mr-2" />
										Add
									</a-button>

									<a-dropdown
										v-if="useOptionMenu && !record.isActive && !record?.editable"
										trigger="click"
									>
										<div>
											<button
												type="button"
												:data-testid="`more-${index}`"
												class="inline-flex items-center justify-center rounded-[8px] p-[20px_33px] shadow-none"
											>
												<lazy-tools-icon icon="more" dir="system" />
											</button>
										</div>

										<template #overlay>
											<a-menu class="light-shadow w-full min-w-[120px] rounded-md !py-0">
												<template v-for="(v, k) in tableMenu" :key="k">
													<a-menu-item
														v-if="v?.condition"
														:data-testid="`${index}-${k}`"
														class="py-4 hover:bg-gray-25 hover:font-medium hover:!text-blue-950"
														@click="v.callback([index, null])"
													>
														<span class="w-full capitalize" :class="v?.spanClass">
															{{ k }}
														</span>
													</a-menu-item>
												</template>
											</a-menu>
										</template>
									</a-dropdown>

									<button
										v-if="!record.isActive && record.done && !useOptionMenu"
										type="submit"
										class="default-btn-sm bg-gray-400 text-white shadow-md disabled:bg-gray-200 disabled:shadow-none"
										:disabled="isRecordEmpty(record)"
										:data-testid="`edit-${index}`"
										@click="editRecord(index)"
									>
										<lazy-tools-icon dir="design" icon="edit" class="mr-2" />
										Edit
									</button>

									<button
										v-else-if="
											deletable &&
											tableRecord.length > 1 &&
											isRecordEmpty(record) &&
											index !== 0 &&
											!record.editable
										"
										type="button"
										:data-testid="`remove-${index}`"
										class="default-btn-sm bg-error-500 text-white disabled:bg-error-200"
										@click="removeField(index)"
									>
										<lazy-tools-icon dir="other" icon="recycle" class="mr-2" />
										Remove
									</button>

									<a-button
										v-else-if="!record.isActive && record.editable"
										class="default-btn-sm !bg-primary-2 !text-white hover:!bg-primary-500 disabled:!bg-primary-100"
										:disabled="isRecordEmpty(record)"
										:loading="record?.loading"
										:data-testid="`done-${index}`"
										@click="updateRecord(index)"
									>
										<lazy-tools-icon
											dir="fill"
											icon="check-2"
											:height="18"
											:width="18"
											path-class="stroke-white fill-transparent"
											class="mr-2"
										/>
										<span>Done</span>
									</a-button>
								</template>
								<button
									v-else
									type="button"
									:data-testid="`added-${index}`"
									class="0 default-btn-sm bg-primary-100 text-white"
									disabled
								>
									<lazy-tools-icon
										dir="fill"
										icon="check-2"
										:height="18"
										:width="18"
										path-class="stroke-white fill-transparent"
										class="mr-2"
									/>
									<span>Added</span>
								</button>
							</div>
							<!-- /=== -->
						</template>
					</shared-dynamic-table>

					<div
						class="fixed bottom-5 left-0 right-0 z-[200] mx-auto flex w-full max-w-[564px] items-center justify-center px-5"
					>
						<a-button
							class="default-btn w-full justify-center bg-primary-2 text-white"
							:disabled="!isAnItemDone || loading || submitting"
							:loading="submitting"
							data-testid="add to plan"
							@click="saveForm"
						>
							{{ submitting ? 'Saving...' : 'Save and Continue' }}
						</a-button>
					</div>
				</a-form>

				<div v-if="viewSlot">
					<slot name="filled-slots" />
				</div>
			</shared-table-loader>
			<div class="flex items-center justify-end">
				<shared-pagination
					v-show="Number(paginationList?.total_pages ?? 0) > 1"
					:current-page="currentPage"
					:total-items="Number(paginationList?.total_pages ?? 0)"
					:per-page="Number(paginationList?.per_page ?? 0)"
					@page-changed="proceedToNextPage"
				/>
			</div>
		</div>
	</div>
</template>

<style scoped>
	.custom-input {
		@apply !h-[70px] !rounded-none border-[0.5px] !border-transparent !px-5 focus:!border-primary-2 disabled:!bg-white disabled:!font-medium disabled:!text-gray-600;
	}
</style>
